import React from "react";
import { ClickableCard, MoreMenu, Text } from "@astryxdesign/core";
import type { DropdownMenuOption } from "@astryxdesign/core";
import styles from "./PreviewCard.module.css";

const MONO_COLORS = [
  "blue",
  "green",
  "purple",
  "teal",
  "pink",
  "orange",
] as const;

// Derive stable initials + a category color from the title so cards are
// visually distinguishable at a glance.
const monogramFor = (title: string): { initials: string; color: string } => {
  const words = title.trim().split(/\s+/).filter(Boolean);
  const initials = (
    words.length >= 2 ? words[0][0] + words[1][0] : title.trim().slice(0, 2)
  ).toUpperCase();

  let hash = 0;
  for (let i = 0; i < title.length; i += 1) {
    hash = (hash * 31 + title.charCodeAt(i)) >>> 0;
  }
  return { initials, color: MONO_COLORS[hash % MONO_COLORS.length] };
};

export interface PreviewCardProps {
  title: string;
  description?: string | null;
  descriptionPlaceholder?: string;
  footer?: React.ReactNode;
  onClick: () => void;
  menuItems?: DropdownMenuOption[];
}

const PreviewCard = ({
  title,
  description,
  descriptionPlaceholder = "No description yet",
  footer,
  onClick,
  menuItems,
}: PreviewCardProps): React.ReactElement => {
  const hasMenu = menuItems !== undefined && menuItems.length > 0;
  const hasDescription = description != null && description.trim().length > 0;
  const { initials, color } = monogramFor(title);

  return (
    <ClickableCard
      label={title}
      onClick={onClick}
      padding={0}
      elevation="low"
      height="100%"
    >
      <div className={styles.card}>
        <div className={styles.header}>
          <span
            className={[styles.monogram, styles[`mono_${color}`]].join(" ")}
            aria-hidden="true"
          >
            {initials}
          </span>
          {hasMenu && (
            <MoreMenu
              items={menuItems}
              label="Template actions"
              variant="ghost"
              size="sm"
            />
          )}
        </div>

        <div className={styles.body}>
          <Text weight="bold" maxLines={1} hasTruncateTooltip>
            {title}
          </Text>
          {hasDescription ? (
            <Text type="supporting" color="secondary" maxLines={2} hasTruncateTooltip={false}>
              {description}
            </Text>
          ) : (
            <Text type="supporting" color="placeholder">
              {descriptionPlaceholder}
            </Text>
          )}
        </div>

        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </ClickableCard>
  );
};

export default PreviewCard;
